import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatDialog } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatTooltipModule } from '@angular/material/tooltip'
import { CopyButtonComponent } from '@saniup-webapps/shared/components'
import { SupportSheetService } from '../../services/support-sheet.service'
import {
  buildHelpMailto,
  SANIUP_ANTWORTZEIT,
  SANIUP_ERREICHBARKEIT,
  SANIUP_HELP_EMAIL,
  SANIUP_TELEFON,
  SANIUP_TELEFON_LINK,
} from '../../util/support'

/**
 * Der Weg zum SaniUp-Support: Telefonnummer und Adresse sichtbar, beide anklickbar und beide
 * kopierbar.
 *
 * **Warum es die Karte gibt.** Bis 2026-08-23 stand an vier Stellen ein nacktes
 * `href="mailto:help@saniup.de"` mit der Aufschrift „Support kontaktieren". Ein Klick darauf reisst
 * ein Mailprogramm auf — oder eben nicht, wenn keins eingerichtet ist, und dann passiert sichtbar
 * gar nichts. Man sieht die Adresse nicht, kann sie nicht kopieren, und eine Telefonnummer stand
 * überhaupt nur in einem eigenen Dialog der Seitenleiste. Urteil Tim 2026-08-23: „dass sich dann
 * dieses Mailing öffnet, ist scheisse".
 *
 * **Deshalb steht hier beides als Text.** Der Link ist das Angebot, der Kopierknopf der Ausweg —
 * am Arbeitsplatz eines Sanitätshauses ist „Nummer abschreiben und mit dem Tischtelefon wählen"
 * der Normalfall, nicht `tel:`.
 *
 * `betreff` und `text` belegen die Mail vor. Ohne sie bleibt es eine leere Mail an den Support —
 * auch richtig, nur weniger hilfreich.
 *
 * **Erst fragen, dann anrufen (`[fragen]="true"`, seit 2026-09-30).** An einer Fehlerstelle steht
 * ueber den Kontaktwegen ein Feld, dessen Frage der KI-Assistent im Support-Sheet beantwortet. Die
 * meisten Anfragen klaeren sich dort sofort (Idee Tim 2026-09-30: „probieren Sie es einfach mal hier
 * einzutippen, in der Regel klaert das schon direkt"), und wer nicht weiterkommt, uebergibt aus dem
 * Sheet mit einem Klick an das Team. Die Kontaktwege bleiben trotzdem sichtbar. **Nur dort einschalten,
 * wo der Assistent helfen kann:** nicht auf dem Boot-Fehler-Bildschirm (dort laeuft die App nicht),
 * nicht beim ausstehenden Zugang (ohne Rolle antwortet der Assistent nicht) und nicht, wo der
 * Anlass ein Gespraech ist (Einrichtung, Termin, Feedback).
 */
@Component({
  selector: 'app-support-card',
  imports: [FormsModule, MatIconModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatTooltipModule, CopyButtonComponent],
  templateUrl: './support-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './support-card.component.scss',
})
export class SupportCardComponent {
  /** Überschrift der Karte. Ersetzbar, wo der Anlass konkreter ist als „Support". */
  readonly titel = input('Wir helfen weiter')

  /** Ein Satz über den Kontaktwegen. Leer lassen, wo der Zusammenhang schon darüber steht. */
  readonly einleitung = input<string>()

  /** Vorbelegter Betreff der Mail. */
  readonly betreff = input('Frage aus SaniUp ONE')

  /** Vorbelegter Text der Mail. */
  readonly text = input<string>()

  protected readonly email = SANIUP_HELP_EMAIL
  protected readonly telefon = SANIUP_TELEFON
  protected readonly telefonLink = SANIUP_TELEFON_LINK
  protected readonly erreichbarkeit = SANIUP_ERREICHBARKEIT
  protected readonly antwortzeit = SANIUP_ANTWORTZEIT

  /**
   * `karte`: Haarlinienkarte mit Nummer und Adresse untereinander (Fehlerstellen, Dialoge).
   * `kacheln`: getönter Block mit zwei Kacheln nebeneinander, für den Fuß des Support-Sheets.
   */
  readonly darstellung = input<'karte' | 'kacheln'>('karte')

  /** Bietet über den Kontaktwegen ein Feld an, dessen Frage der KI-Assistent beantwortet. */
  readonly fragen = input(false)

  /**
   * Worum es an dieser Stelle geht, in wenigen Worten. Steht vor der getippten Frage, damit der
   * Assistent weiss, woher sie kommt; ohne Anlass geht die Frage unveraendert raus.
   */
  readonly anlass = input<string>()

  private readonly supportSheet = inject(SupportSheetService)
  private readonly dialog = inject(MatDialog)
  protected readonly frage = signal('')

  protected readonly mailto = computed(() => buildHelpMailto(this.betreff(), this.text()))

  /**
   * Die Frage geht ins Support-Sheet. Ein offener Dialog schliesst dabei: das Sheet liegt sonst
   * unter seinem Abdunkler und ist nicht zu bedienen. Die Karte steht nur an Stellen, an denen der
   * Dialog ohnehin nicht weiterkommt.
   */
  protected frageStellen(event: Event): void {
    event.preventDefault()
    const text = this.frage().trim()
    if (!text) {
      return
    }
    const anlass = this.anlass()
    this.dialog.closeAll()
    this.supportSheet.oeffnen(anlass ? `${anlass}: ${text}` : text)
    this.frage.set('')
  }
}
